import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { TopNavBarLinkItems } from '../../core/configs/nav-bar-link-items.config';
import IIremboUser, {
  _BLANK_PROFILE,
} from '../../core/models/irembo-user-model';
import { INavbarTopLinkItem } from '../../core/models/navbar-link-item.model';
import { AuthService } from '../../core/services/auth.service';
import {
  IdleTimerService,
  RoleEnumToLabelUtil,
  RoleLabelToEnumUtil,
  RolesEnum,
} from '@irembo-andela/irembogov3-common';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { IProfilePictureResponse } from '../../core/models/users/profile-picture.model';
import { ProfileService } from '../../core/services/profile.service';

@Component({
  standalone: false,
  selector: 'irembogov-dashboard',
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit, OnDestroy {
  userProfile: IIremboUser = _BLANK_PROFILE;
  _userProfile!: Subscription;
  isLoggedIn!: boolean;

  navBarItems: INavbarTopLinkItem[] = [];

  idleState!: string;
  profilePicture?: IProfilePictureResponse;
  profilePictureSubscription = this.profilePictureService.profilePictureSubject;

  private userRoles: RolesEnum[] = [];
  RoleEnumToLabelUtil = RoleEnumToLabelUtil;
  userActiveRole: RolesEnum = RolesEnum.ROLE_INTEGRATION_MANAGER;

  userActiveRoleSubject$: Subject<void> = new Subject<void>();

  constructor(
    private authService: AuthService,
    private idleTimerService: IdleTimerService,
    private router: Router,
    private profilePictureService: ProfileService
  ) {}

  async ngOnInit() {
    this.isLoggedIn = await this.authService.isLoggedIn();

    this.initializeRoles();
    this.loadUserOtherRoles();

    this.profilePictureSubscription.subscribe(profilePicture => {
      if (profilePicture) {
        this.profilePicture = profilePicture;
      }
    });

    this._userProfile = this.authService.userProfile$.subscribe(
      (profile: IIremboUser) => {
        this.userProfile = profile;
        this.getProfilePicture();
      }
    );
    await this.authService.checkLoginProfile();
    this.idleTimerService.initIdleTimer(
      environment.idleTimeOutDuration,
      environment.idleTimeOutCountDown
    );

    this.idleTimerService.idleState$.subscribe((state: string) => {
      if (state === 'timedOut') {
        this.logoutUser();
      }
    });
  }

  private async loadUserOtherRoles(): Promise<void> {
    this.userRoles = await this.authService.loadUserIremboRoles();
  }

  logoutUser() {
    this.authService.logOutUser();
  }

  getTopNavBarLinkItemsForRole(): INavbarTopLinkItem[] {
    const navbarItems: INavbarTopLinkItem[] = [];
    Object.keys(TopNavBarLinkItems).forEach((key: string) => {
      const item: INavbarTopLinkItem = TopNavBarLinkItems[key];

      if (!item.allowedRoles) {
        navbarItems.push(item);
        return;
      }

      if (!this.userActiveRole) return;

      if (item.allowedRoles.indexOf(this.userActiveRole) > -1) {
        navbarItems.push(item);
        return;
      }

      return false;
    });

    return navbarItems;
  }

  getOtherRoles(): string[] {
    const allowedRoles = [
      RoleEnumToLabelUtil(RolesEnum.ROLE_INTEGRATION_MANAGER),
      RoleEnumToLabelUtil(RolesEnum.ROLE_INTEGRATION_OPS),
    ] as string[];
    return this.userRoles
      .map((role: RolesEnum) => RoleEnumToLabelUtil(role))
      .filter((role: string) => allowedRoles.includes(role));
  }

  onRoleChange(roleLabel: string) {
    this.userActiveRole = RoleLabelToEnumUtil(roleLabel);
    this.authService.setUserRole(this.userActiveRole);

    // redirect to /dashboard
    this.router.navigate(['/dashboard']);
  }

  async initializeRoles() {
    // Fetch and handle roles in ngOnInit
    const firstAllowedRole = await this.authService.firstAllowedRole();

    if (firstAllowedRole) {
      this.userActiveRole = firstAllowedRole;
      // Publish the resolved role into the shared stream so role-gated features
      // (e.g. Background Jobs) work on first load — without this, userActiveRole$
      // stays _UNKNOWN_ROLE_ until the user manually picks a role from the dropdown,
      // hiding every role-gated action button.
      this.authService.setUserRole(firstAllowedRole);
    }
  }

  ngOnDestroy(): void {
    if (this._userProfile) this._userProfile.unsubscribe();
    this.profilePictureSubscription.unsubscribe();
  }

  handleProfileClick() {
    this.router.navigate(['/dashboard/users/account-profile']);
  }

  getProfilePicture() {
    this.authService.getProfilePicture(this.userProfile?.id ?? '').subscribe({
      next: response => {
        this.profilePicture = response.data;
      },
    });
  }
}
